import { axiosClient } from '@/shared/apis/config';
import { ApiResponse, ErrorResponse } from '@/shared/types';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';

// ============================================================================
// Types
// ============================================================================

export interface AiSceneTextItem {
  text: string;
  top: number;
  left: number;
  width: number;
  height: number;
  opacity: number;
  fontSize: number;
  color: string;
  align: string;
  fontFamily: string | null;
  animationType: string | null;
  fadeInSeconds: number;
  fadeOutSeconds: number;
  delaySeconds: number;
}

export interface AiSceneBackground {
  color: string;
  transparent?: boolean;
}

export interface AiSceneSegment {
  startSeconds: number | null;
  durationSeconds: number;
  background: AiSceneBackground;
  textItems: AiSceneTextItem[];
  transitionType: string | null;
  transitionDirection: string | null;
}

export interface AiSceneResult {
  segments: AiSceneSegment[];
  totalDurationSeconds: number;
  title: string;
}

export interface AiSceneGenerateRequest {
  projectId: number;
  prompt: string;
  style?: string | null;
  language?: string;
  maxDurationSeconds?: number;
  aspectRatio?: string;
}

export interface AiSceneGenerateResponse {
  sceneResult: AiSceneResult;
  clipInfos: Record<string, unknown>[];
  chatResponse: string;
}

type ResponseType = ApiResponse<AiSceneGenerateResponse>;

// ============================================================================
// API Function
// ============================================================================

export const postAiSceneGenerate = async (request: AiSceneGenerateRequest) => {
  const response = await axiosClient.post<ResponseType>(
    '/v2.0/video-editor/editor/generate',
    request,
  );
  return response.data;
};

// ============================================================================
// React Query Hook
// ============================================================================

export const usePostAiSceneGenerateApi = () => {
  return useMutation<
    ResponseType,
    AxiosError<ErrorResponse>,
    AiSceneGenerateRequest
  >({
    mutationFn: postAiSceneGenerate,
  });
};

// ============================================================================
// Modify Types
// ============================================================================

export interface AiItemCurrentState {
  id: string;
  type: string;
  // 공통
  fromFrame?: number;
  durationInFrames?: number;
  top?: number;
  left?: number;
  width?: number;
  height?: number;
  opacity?: number;
  // 애니메이션/페이드
  animationType?: string | null;
  fadeInSeconds?: number;
  fadeOutSeconds?: number;
  // text / captions
  text?: string;
  fontSize?: number;
  color?: string;
  align?: string;
  fontFamily?: string;
  highlightColor?: string | null;
  // video / audio / gif
  playbackRate?: number;
  decibelAdjustment?: number;
  // blur
  blurAmount?: number;
}

export interface AiItemAction {
  action: 'update' | 'create' | 'delete';
  id: string;
  // create 시 필수
  type?: string | null;
  fromFrame?: number | null;
  durationInFrames?: number | null;
  // 공통
  top?: number | null;
  left?: number | null;
  width?: number | null;
  height?: number | null;
  opacity?: number | null;
  // 애니메이션/페이드
  animationType?: string | null;
  fadeInSeconds?: number | null;
  fadeOutSeconds?: number | null;
  // text / captions
  text?: string | null;
  fontSize?: number | null;
  color?: string | null;
  align?: string | null;
  fontFamily?: string | null;
  highlightColor?: string | null;
  // Phase 2: 확장 텍스트 스타일
  fontStyle?: { variant: string; weight: string } | null;
  lineHeight?: number | null;
  letterSpacing?: number | null;
  strokeWidth?: number | null;
  strokeColor?: string | null;
  direction?: string | null;
  maxLines?: number | null;
  shadow?: {
    color: string;
    opacity: number;
    visible: boolean;
    offsetX: number;
    offsetY: number;
    blur: number;
  } | null;
  background?: {
    color: string;
    opacity: number;
    visible: boolean;
    horizontalPadding: number;
    verticalPadding: number;
    borderRadius: number;
    mode: string;
    verticalOffset: number;
  } | null;
  // Phase 2: 위치/레이아웃 확장
  rotation?: number | null;
  borderRadius?: number | null;
  cropLeft?: number | null;
  cropTop?: number | null;
  cropRight?: number | null;
  cropBottom?: number | null;
  // video / audio / gif
  playbackRate?: number | null;
  decibelAdjustment?: number | null;
  audioFadeInSeconds?: number | null;
  audioFadeOutSeconds?: number | null;
  // blur
  blurAmount?: number | null;
}

export interface AiTransitionCurrentState {
  id: string;
  transitionType: string;
  direction?: string | null;
  durationInFrames: number;
  sourceItemId: string;
  targetItemId: string;
  trackId: string;
}

export interface AiTransitionChange {
  action: 'add' | 'update' | 'delete';
  sourceItemId: string;
  targetItemId: string;
  transitionType?: string | null;
  direction?: string | null;
  transitionId?: string | null;
  durationInFrames?: number | null;
}

export interface AiSoundEffectAction {
  action: 'add' | 'delete';
  soundEffectId?: string | null;
  fromFrame?: number | null;
  itemId?: string | null;
}

export interface AiModifyResult {
  items: AiItemAction[];
  transitions: AiTransitionChange[];
  soundEffects?: AiSoundEffectAction[];
  explanation: string;
}

export interface AiSceneModifyRequest {
  projectId: number;
  prompt: string;
  items: AiItemCurrentState[];
  transitions?: AiTransitionCurrentState[];
  language?: string;
}

export interface AiSceneModifyResponse {
  modifyResult: AiModifyResult;
  chatResponse: string;
}

type ModifyResponseType = ApiResponse<AiSceneModifyResponse>;

// ============================================================================
// Modify API Function
// ============================================================================

export const postAiSceneModify = async (request: AiSceneModifyRequest) => {
  const response = await axiosClient.post<ModifyResponseType>(
    '/v2.0/video-editor/editor/modify',
    request,
  );
  return response.data;
};

// ============================================================================
// Modify React Query Hook
// ============================================================================

export const usePostAiSceneModifyApi = () => {
  return useMutation<
    ModifyResponseType,
    AxiosError<ErrorResponse>,
    AiSceneModifyRequest
  >({
    mutationFn: postAiSceneModify,
  });
};

// ============================================================================
// Code Generate Types
// ============================================================================

export interface AiCodeGenerateRequest {
  projectId: number;
  prompt: string;
  compositionWidth?: number;
  compositionHeight?: number;
  fps?: number;
  language?: string;
  maxDurationSeconds?: number;
  referenceImageUrl?: string | null;
}

export interface AiCodeGenerateResponse {
  code: string;
  title: string;
  durationInFrames: number;
  chatResponse: string;
}

type CodeGenerateResponseType = ApiResponse<AiCodeGenerateResponse>;

// ============================================================================
// Code Generate API Function
// ============================================================================

export const postAiCodeGenerate = async (request: AiCodeGenerateRequest) => {
  const response = await axiosClient.post<CodeGenerateResponseType>(
    '/v2.0/video-editor/remotion-code',
    request,
  );
  return response.data;
};

// ============================================================================
// Code Generate React Query Hook
// ============================================================================

export const usePostAiCodeGenerateApi = () => {
  return useMutation<
    CodeGenerateResponseType,
    AxiosError<ErrorResponse>,
    AiCodeGenerateRequest
  >({
    mutationFn: postAiCodeGenerate,
  });
};
